/**
 * Reservations (Phase F).
 *
 * Read-mostly view of `GET /reservations/:chargerId`. Booking is still done
 * via the existing scheduling flow on the ChargingStation screen, which the
 * watchdog auto-expires on its own. This tab adds:
 *   - explicit status enum colouring
 *   - cancel affordance (DELETE /reservations/:chargerId/:reservationId)
 */

import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import OcppActionButton from '../components/OcppActionButton';
import OcppEmptyState from '../components/OcppEmptyState';
import OcppErrorBanner from '../components/OcppErrorBanner';
import OcppLoadingState from '../components/OcppLoadingState';
import OcppSectionCard from '../components/OcppSectionCard';
import OcppStatusPill from '../components/OcppStatusPill';
import { OcppTheme } from '../components/OcppTheme';
import { cancelReservation, listReservations } from '../services';
import { isOcppSuccess, mapOcppApiError, type OcppApiErrorInfo } from '../utils/apiErrors';
import { formatOcppTimestamp, reservationStatusColor } from '../utils/statusFormat';
import type { ListOcppReservationsResponse, OcppReservation } from '../models';
import type { OcppTabProps } from './types';

const ReservationsTab: React.FC<OcppTabProps> = ({ chargerId }) => {
  const [rows, setRows] = useState<OcppReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<OcppApiErrorInfo | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listReservations(chargerId);
    if (isOcppSuccess(res.status)) {
      const payload = res.payload as ListOcppReservationsResponse | null;
      setRows(payload?.reservations ?? []);
      setError(null);
    } else {
      setError(mapOcppApiError(res.status, res.payload));
    }
    setLoading(false);
  }, [chargerId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const onCancel = useCallback(
    async (reservation: OcppReservation) => {
      setBusyId(reservation.reservation_id);
      const res = await cancelReservation(chargerId, reservation.reservation_id);
      if (isOcppSuccess(res.status)) {
        await load();
      } else {
        setError(mapOcppApiError(res.status, res.payload));
      }
      setBusyId(null);
    },
    [chargerId, load]
  );

  if (loading) return <OcppLoadingState label="Loading reservations…" />;
  if (error) return <OcppErrorBanner error={error} onRetry={load} />;

  if (rows.length === 0) {
    return (
      <OcppEmptyState
        icon="event"
        title="No reservations"
        description="When drivers book this charger, their reservations show up here."
      />
    );
  }

  return (
    <View>
      {rows.map((row) => (
        <OcppSectionCard
          key={row.id}
          title={`Reservation #${row.reservation_id}`}
          subtitle={`Expires ${formatOcppTimestamp(row.expiry_date)}`}
          trailing={<OcppStatusPill label={row.status} color={reservationStatusColor(row.status)} />}
        >
          <Text style={styles.metaLine}>
            {row.scope === 'chargepoint' ? 'Whole charger' : `EVSE ${row.evse_id} · Connector ${row.connector_id}`}
          </Text>
          <Text style={styles.metaLine}>id_tag: {row.id_tag}</Text>
          {row.parent_id_tag ? (
            <Text style={styles.metaLine}>parent_id_tag: {row.parent_id_tag}</Text>
          ) : null}
          {row.status === 'active' ? (
            <OcppActionButton
              label="Cancel reservation"
              variant="danger"
              icon="event-busy"
              loading={busyId === row.reservation_id}
              onPress={() => onCancel(row)}
              style={{ alignSelf: 'flex-start', marginTop: 12 }}
            />
          ) : null}
        </OcppSectionCard>
      ))}
    </View>
  );
};

export default ReservationsTab;

const styles = StyleSheet.create({
  metaLine: {
    color: OcppTheme.textSecondary,
    fontSize: 13,
    marginTop: 2,
  },
});
